import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getMyBusiness, updateMyBusiness } from "services/businessService";
import Card from "components/card";
import Button from "components/button";
import { useToast } from "components/Toast";

interface BusinessData {
  id?: number;
  name: string;
  description: string;
  category: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  status?: string;
}

const BusinessEdit: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [business, setBusiness] = useState<BusinessData | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    phone: "",
    email: "",
    website: "",
    address: "",
  });

  const categories = [
    "Agriculture",
    "Manufacturing",
    "Trading",
    "Services",
    "Technology",
    "Health",
    "Education",
    "Hospitality",
    "Transportation",
    "Other",
  ];

  const fetchBusiness = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMyBusiness();
      if (data) {
        setBusiness(data);
        setFormData({
          name: data.name || "",
          description: data.description || "",
          category: data.category || "",
          phone: data.phone || "",
          email: data.email || "",
          website: data.website || "",
          address: data.address || "",
        });
      }
    } catch (err: any) {
      const message =
        err.response?.status === 404
          ? "You don't have an active business listing. Create one first."
          : err.response?.data?.message || "Failed to load business details";
      toast.showError(message);
      if (err.response?.status === 404) {
        navigate("/user/businesses");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    fetchBusiness();
  }, [fetchBusiness]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.description || !formData.category) {
      toast.showError("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await updateMyBusiness(formData);
      toast.showSuccess(
        "Business listing updated successfully! Admin will review the changes."
      );
      navigate("/user/businesses");
    } catch (err: any) {
      console.error(err);
      const message =
        err.response?.data?.message || "Failed to update business listing";
      toast.showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="mb-4 inline-block animate-spin rounded-full border-4 border-gray-300 border-t-blue-500"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading business details...
          </p>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="flex items-center justify-center p-8">
        <Card extra="w-full max-w-md p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            Unable to load business details
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
          Edit Business Listing
        </h1>
        <button
          onClick={() => navigate("/user/businesses")}
          className="text-blue-500 hover:underline dark:text-blue-400"
        >
          ← Back to Businesses
        </button>
      </div>

      {business.status === "pending" && (
        <Card extra="w-full mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            ⏳ Your business listing is pending approval. Changes will be
            reviewed again.
          </p>
        </Card>
      )}

      {business.status === "rejected" && (
        <Card extra="w-full mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-800 dark:text-red-200">
            ❌ Your business listing was rejected. Please update it and resubmit
            for approval.
          </p>
        </Card>
      )}

      <Card extra="w-full p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Business Name */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Business Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your business name"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              required
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your business, products, and services"
              rows={6}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Minimum 50 characters recommended
            </p>
          </div>

          {/* Phone */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="e.g., +234 800 123 4567"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Email */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="e.g., business@example.com"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Website */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Website (Optional)
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="e.g., https://example.com"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Address */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Business Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter your business location"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={submitting}
              variant="primary"
              className="flex-1"
            >
              {submitting ? "Updating..." : "Update Business Listing"}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/user/businesses")}
              className="flex-1"
            >
              <Button type="button" variant="outline" className="w-full">
                Cancel
              </Button>
            </button>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            * Required fields. Your changes will be reviewed by admin before
            publication.
          </p>
        </form>
      </Card>
    </div>
  );
};

export default BusinessEdit;
